/**
 * Profile Page Component
 * 
 * User profile management page
 * - Display user information (email, name)
 * - Display associated riders
 * - Implement profile update form
 * - Implement rider association
 * 
 * Requirements: 6.1, 6.2, 6.3
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useRidersStore } from '../store/ridersStore';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { restClient, Rider, UpdateUserDto } from '../api/rest-client';
import { formatPhoneForDisplay, formatPhoneForStorage } from '../utils/phone';
import { formatRiderName, getUserRoleBadgeColor } from '../utils/riderDisplay';
import PasswordChangeModal from '../components/auth/PasswordChangeModal';
import '../auth/amplify-config'; // Initialize Amplify

export default function Profile() {
  const navigate = useNavigate();
  
  // Auth state
  const { user } = useAuth();
  
  // Riders state
  const { filteredRiders, fetchRiders } = useRidersStore();
  
  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
  });
  
  // Displayed profile values (overrides user object after updates)
  const [displayedProfile, setDisplayedProfile] = useState<{
    name?: string;
    phone?: string;
  }>({});
  
  // Associated riders state
  const [associatedRiders, setAssociatedRiders] = useState<Array<Rider & { statsBySeason?: Record<string, { total_hours: number; event_count: number }> }>>([]);
  const [availableRiders, setAvailableRiders] = useState<Rider[]>([]);
  const [isAssociating, setIsAssociating] = useState(false);
  const [selectedRiderId, setSelectedRiderId] = useState<string>('');

  /**
   * Initialize form data when user loads
   * Requirements: 6.1
   */
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || user.given_name || '',
        phone: user.phone ? formatPhoneForDisplay(user.phone) : '',
      });
    }
  }, [user]);

  /**
   * Fetch riders on mount
   */
  useEffect(() => {
    // User is already loaded from AuthProvider
    if (!user) {
      // AuthProvider will load the user automatically
    }
    
    fetchRiders();
    
    // Fetch user's associated riders from dashboard API
    if (user) {
      fetchAssociatedRidersFromDashboard();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchRiders, user]);

  /**
   * Fetch associated riders from dashboard API (same as Dashboard page)
   */
  const fetchAssociatedRidersFromDashboard = useCallback(async () => {
    try {
      const dashboardData = await restClient.getDashboard();
      
      if (dashboardData.my_riders && Array.isArray(dashboardData.my_riders.riders)) {
        // Use participation API — one call per rider returns available_seasons + all events
        const ridersWithStats = await Promise.all(
          dashboardData.my_riders.riders.map(async (rider: Rider) => {
            const statsBySeason: Record<string, { total_hours: number; event_count: number }> = {};
            try {
              // Single call with no season filter returns all participation data
              const participation = await restClient.getRiderParticipation(rider.rider_id);
              const currentYear = new Date().getFullYear().toString();
              const seasons = new Set(participation.available_seasons || []);
              seasons.add(currentYear); // Always include current season
              
              // Compute stats per season from the events list
              for (const season of seasons) {
                const seasonEvents = (participation.events || []).filter((evt: any) => {
                  const date = evt.event_date || evt.event_starttime || '';
                  return date.startsWith(season);
                });
                const totalHours = seasonEvents.reduce((sum: number, evt: any) => sum + (evt.duration_hours || 0), 0);
                statsBySeason[season] = {
                  event_count: seasonEvents.length,
                  total_hours: Math.round(totalHours * 10) / 10,
                };
              }
            } catch {
              // Fallback: current season with zeros
              statsBySeason[new Date().getFullYear().toString()] = { total_hours: 0, event_count: 0 };
            }
            return { ...rider, statsBySeason };
          })
        );
        
        setAssociatedRiders(ridersWithStats);
      } else {
        setAssociatedRiders([]);
      }
    } catch (err) {
      setAssociatedRiders([]);
    }
  }, []);

  /**
   * Filter available riders when data changes
   * Requirements: 6.1
   */
  useEffect(() => {
    if (user && filteredRiders.length > 0) {
      // Filter riders not yet associated (exclude riders in associatedRiders)
      const associatedRiderIds = new Set(associatedRiders.map(r => r.rider_id));
      const available = filteredRiders.filter(rider => !associatedRiderIds.has(rider.rider_id));
      
      setAvailableRiders(available);
    }
  }, [user, filteredRiders, associatedRiders]);

  /**
   * Handle form input changes
   */
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  /**
   * Handle profile update form submission
   * Requirements: 6.2
   */
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) return;
    
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Validate and format phone number if provided
      let formattedPhone: string | undefined = undefined;
      if (formData.phone) {
        try {
          formattedPhone = formatPhoneForStorage(formData.phone);
        } catch (phoneError) {
          setError(phoneError instanceof Error ? phoneError.message : 'Invalid phone number format');
          setIsSaving(false);
          return;
        }
      }

      const updateData: UpdateUserDto = {
        username: user.username,
        email: user.email, // API expects email field
        name: formData.name,
        phone: formattedPhone,
      };
      
      await restClient.updateUser(user.username, updateData);
      
      // Update the displayed profile values to show changes immediately
      setDisplayedProfile({
        name: formData.name,
        phone: formattedPhone,
      });
      
      // Update the form data to reflect the changes
      setFormData({
        name: formData.name,
        phone: formattedPhone ? formatPhoneForDisplay(formattedPhone) : '',
      });
      
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  }, [user, formData]);

  /**
   * Handle rider association
   * Requirements: 6.3
   */
  const handleAssociateRider = useCallback(async () => {
    if (!user || !selectedRiderId) return;
    
    setIsAssociating(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Get the selected rider
      const rider = availableRiders.find(r => r.rider_id === selectedRiderId);
      if (!rider) {
        throw new Error('Rider not found');
      }
      
      // Parse existing associated users or create new array
      let associatedUsers: Array<{ user_id: string; role: string }> = [];
      if (rider.associatedUsers) {
        try {
          associatedUsers = JSON.parse(rider.associatedUsers);
          // Handle legacy format (array of strings)
          if (associatedUsers.length > 0 && typeof associatedUsers[0] === 'string') {
            associatedUsers = (associatedUsers as any).map((userId: string) => ({
              user_id: userId,
              role: 'Self'
            }));
          }
        } catch {
          associatedUsers = [];
        }
      }
      
      // Add current user to associated users if not already there
      const userEmail = user.email || user.username;
      if (!associatedUsers.find(a => a.user_id === userEmail || a.user_id === user.username)) {
        associatedUsers.push({
          user_id: userEmail,
          role: 'Self'
        });
      }
      
      // Update rider with new associated users
      await restClient.updateRider(selectedRiderId, {
        rider_id: selectedRiderId,
        associatedUsers: JSON.stringify(associatedUsers),
      });
      
      // Refresh riders data and associated riders from dashboard
      await fetchRiders();
      await fetchAssociatedRidersFromDashboard();
      
      setSuccess('Rider associated successfully');
      setSelectedRiderId('');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to associate rider');
    } finally {
      setIsAssociating(false);
    }
  }, [user, selectedRiderId, availableRiders, fetchRiders, fetchAssociatedRidersFromDashboard]);

  /**
   * Cancel editing
   */
  const handleCancelEdit = useCallback(() => {
    if (user) {
      setFormData({
        name: user.name || user.given_name || '',
        phone: user.phone || '',
      });
    }
    setIsEditing(false);
    setError(null);
  }, [user]);

  if (!user) {
    return (
      <div className="min-h-screen bg-primary-50">
        <div className="bg-white shadow">
          <div className="container mx-auto px-4 py-4">
            <h1 className="text-3xl font-bold text-primary-900">My Profile</h1>
          </div>
        </div>
        <div className="container mx-auto px-2 md:px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {error ? (
              <Card>
                <div className="text-center py-8">
                  <svg 
                    className="w-16 h-16 mx-auto mb-4 text-danger-500" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                    />
                  </svg>
                  <p className="text-danger-700 mb-4">{error}</p>
                  <Button onClick={() => window.location.reload()}>
                    Refresh Page
                  </Button>
                </div>
              </Card>
            ) : (
              <Card>
                <div className="flex items-center justify-center py-12">
                  <LoadingSpinner size="lg" />
                  <span className="ml-3 text-primary-600">Loading profile...</span>
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="container mx-auto px-4 py-4">
          <h1 className="text-3xl font-bold text-primary-900">My Profile</h1>
          <p className="text-primary-600 mt-1">
            Manage your account information and associated riders
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-2 md:px-4 pt-3 pb-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Success Message */}
          {success && (
            <div className="bg-secondary-100 border border-secondary-400 text-secondary-800 px-4 py-3 rounded-lg">
              {success}
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-danger-100 border border-danger-400 text-danger-800 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* Profile Information Card */}
          <Card>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
              <h2 className="text-2xl font-semibold text-primary-900">
                Profile Information
              </h2>
              {!isEditing && (
                <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                  <Button
                    variant="secondary"
                    onClick={() => setShowPasswordModal(true)}
                    data-testid="change-password-button"
                    className="w-full sm:w-auto"
                  >
                    Change Password
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => setIsEditing(true)}
                    data-testid="edit-profile-button"
                    className="w-full sm:w-auto"
                  >
                    Edit Profile
                  </Button>
                </div>
              )}
            </div>

            {!isEditing ? (
              // View Mode
              <div className="space-y-4" data-testid="profile-view">
                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    Email
                  </label>
                  <p className="text-primary-900">{user.email}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    Name
                  </label>
                  <p className="text-primary-900">
                    {displayedProfile.name ?? user.name ?? user.given_name ?? 'Not set'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    Phone
                  </label>
                  <p className="text-primary-900">
                    {displayedProfile.phone ? formatPhoneForDisplay(displayedProfile.phone) : (user.phone ? formatPhoneForDisplay(user.phone) : 'Not set')}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-1">
                    Role
                  </label>
                  <div className="flex gap-2">
                    {(() => {
                      const badge = getUserRoleBadgeColor(user.groups);
                      return (
                        <span className={`px-3 py-1 ${badge.color} rounded-full text-sm font-semibold`}>
                          {badge.text}
                        </span>
                      );
                    })()}
                  </div>
                </div>
              </div>
            ) : (
              // Edit Mode
              <form onSubmit={handleSubmit} data-testid="profile-form">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-primary-700 mb-1">
                      Email
                    </label>
                    <p className="text-primary-600 text-sm">
                      {user.email} (cannot be changed)
                    </p>
                  </div>

                  <div>
                    <label 
                      htmlFor="name" 
                      className="block text-sm font-medium text-primary-700 mb-1"
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      data-testid="name-input"
                    />
                  </div>

                  <div>
                    <label 
                      htmlFor="phone" 
                      className="block text-sm font-medium text-primary-700 mb-1"
                    >
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="(555) 123-4567"
                      data-testid="phone-input"
                    />
                  </div>

                  <div className="flex gap-3 pt-4">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSaving}
                      data-testid="save-button"
                    >
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleCancelEdit}
                      disabled={isSaving}
                      data-testid="cancel-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </Card>

          {/* Associated Riders Card */}
          <Card>
            <h2 className="text-2xl font-semibold text-primary-900 mb-6">
              Associated Riders
            </h2>

            {associatedRiders.length === 0 ? (
              <div className="text-center py-8 text-primary-600">
                <svg 
                  className="w-16 h-16 mx-auto mb-4 text-primary-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" 
                  />
                </svg>
                <p>No associated riders</p>
                <p className="text-sm mt-2">
                  Associate riders below to track their progress
                </p>
              </div>
            ) : (
              <div className="space-y-3 mb-6" data-testid="associated-riders-list">
                {associatedRiders.map(rider => {
                  const isCoach = rider.type === 'Coach' || rider.category === 'Coach';
                  const coachLevel = isCoach ? (rider.level ? `Coach Level ${rider.level}` : 'Coach Level MISSING') : null;
                  
                  return (
                    <button
                      key={rider.rider_id}
                      className="w-full p-4 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors text-left"
                      onClick={() => navigate(`/riders/${rider.rider_id}`)}
                      data-testid="associated-rider-item"
                    >
                      <div className="space-y-1">
                        <h3 className="font-semibold text-primary-900 text-xl">
                          {formatRiderName(rider)}
                        </h3>
                        {rider.birthday && (
                          <p className="text-base text-primary-600">
                            Birthday: {(() => {
                              const [y, m, d] = rider.birthday.split('-');
                              return `${m}/${d}/${y}`;
                            })()}
                          </p>
                        )}
                        {coachLevel ? (
                          <>
                            <p className="text-base text-primary-600">
                              {coachLevel}
                            </p>
                            <p className="text-base text-primary-600">
                              Group: {rider.group}
                            </p>
                          </>
                        ) : (
                          <>
                            {rider.grade && (
                              <p className="text-base text-primary-600">
                                Grade: {rider.grade}
                              </p>
                            )}
                            <p className="text-base text-primary-600">
                              Category: {rider.category}
                            </p>
                            <p className="text-base text-primary-600">
                              Group: {rider.group}
                            </p>
                          </>
                        )}
                        <div className="text-base text-primary-600 mt-1">
                          <span className="font-medium">Attendance:</span>
                          {rider.statsBySeason && Object.keys(rider.statsBySeason).length > 0 ? (
                            <div className="ml-2 mt-1 space-y-0.5">
                              {Object.keys(rider.statsBySeason)
                                .sort((a, b) => parseInt(b) - parseInt(a))
                                .map(season => {
                                  const s = rider.statsBySeason![season];
                                  return (
                                    <div key={season} className="text-sm">
                                      {season}: {s.event_count} Events | {s.total_hours} Hrs
                                    </div>
                                  );
                                })}
                            </div>
                          ) : (
                            <span className="ml-1">No attendance data</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Associate New Rider */}
            {availableRiders.length > 0 && (
              <div className="pt-6 border-t border-primary-200">
                <h3 className="text-lg font-semibold text-primary-900 mb-4">
                  Associate a Rider
                </h3>
                <div className="flex gap-3">
                  <select
                    value={selectedRiderId}
                    onChange={(e) => setSelectedRiderId(e.target.value)}
                    className="flex-1 px-3 py-2 border border-primary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    data-testid="rider-select"
                  >
                    <option value="">Select a rider...</option>
                    {availableRiders.map(rider => (
                      <option key={rider.rider_id} value={rider.rider_id}>
                        {formatRiderName(rider)} - {rider.category} - {rider.group}
                      </option>
                    ))}
                  </select>
                  <Button
                    variant="primary"
                    onClick={handleAssociateRider}
                    disabled={!selectedRiderId || isAssociating}
                    data-testid="associate-button"
                  >
                    {isAssociating ? 'Associating...' : 'Associate'}
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* Password Change Modal */}
      <PasswordChangeModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
}
